// ============================================
// Calibration Case Firestore Service
//
// Persistence for paired SOAP+Lab calibration cases
// used to validate the Captain before launch.
//
// Collection: calibration_cases/{caseId}
// ============================================

import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc,
  query, where, orderBy, limit as firestoreLimit,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { C_CALIBRATION_CASES } from '../../constants/collections';
import type {
  CalibrationCase,
  CalibrationCaseStatus,
} from '../../types/labUpload';

// ── Collection ref ──

const casesCol = () => collection(db, C_CALIBRATION_CASES);

// ── CRUD ──

/** Create a new calibration case. Returns the caseId. */
export async function createCalibrationCase(
  c: CalibrationCase,
): Promise<string> {
  const id = c.caseId;

  await setDoc(doc(casesCol(), id), {
    ...stripUndefined(c),
    _updatedAt: Timestamp.now(),
  });

  return id;
}

/** Fetch a single calibration case by ID. */
export async function getCalibrationCase(
  caseId: string,
): Promise<CalibrationCase | null> {
  const snap = await getDoc(doc(casesCol(), caseId));
  return snap.exists() ? (snap.data() as CalibrationCase) : null;
}

/** Partial update of a calibration case. */
export async function updateCalibrationCase(
  caseId: string,
  updates: Partial<CalibrationCase>,
): Promise<void> {
  await updateDoc(doc(casesCol(), caseId), {
    ...stripUndefined(updates),
    _updatedAt: Timestamp.now(),
  });
}

/** Get calibration cases for a brand, with optional status filter. */
export async function getCalibrationCases(
  brandId: string,
  filters?: { status?: CalibrationCaseStatus },
): Promise<CalibrationCase[]> {
  const constraints: Parameters<typeof query>[1][] = [
    where('brandId', '==', brandId),
    orderBy('importedAt', 'desc'),
  ];

  if (filters?.status) {
    constraints.push(where('status', '==', filters.status));
  }

  const snap = await getDocs(query(casesCol(), ...constraints));
  return snap.docs.map((d) => d.data() as CalibrationCase);
}

/** Get aggregate stats for calibration cases. */
export async function getCalibrationStats(
  brandId: string,
): Promise<{
  total: number;
  byStatus: Record<string, number>;
  avgAlignment: number;
  reviewedCount: number;
}> {
  const cases = await getCalibrationCases(brandId);

  const byStatus: Record<string, number> = {};
  let totalAlignment = 0;
  let scoredCount = 0;
  let reviewedCount = 0;

  for (const c of cases) {
    byStatus[c.status] = (byStatus[c.status] || 0) + 1;

    if (c.alignmentScore !== undefined) {
      totalAlignment += c.alignmentScore;
      scoredCount++;
    }

    if (c.drCReview) {
      reviewedCount++;
    }
  }

  return {
    total: cases.length,
    byStatus,
    avgAlignment: scoredCount > 0 ? Math.round(totalAlignment / scoredCount) : 0,
    reviewedCount,
  };
}

/** Get all reviewed cases (for convergence analysis). */
export async function getReviewedCases(
  brandId: string,
  limit?: number,
): Promise<CalibrationCase[]> {
  const constraints: Parameters<typeof query>[1][] = [
    where('brandId', '==', brandId),
    where('status', 'in', ['reviewed', 'complete']),
    orderBy('importedAt', 'desc'),
  ];

  if (limit) {
    constraints.push(firestoreLimit(limit));
  }

  const snap = await getDocs(query(casesCol(), ...constraints));
  return snap.docs.map((d) => d.data() as CalibrationCase);
}

// ── Utils ──

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function stripUndefined(obj: any): any {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripUndefined);
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = (value !== null && typeof value === 'object')
        ? stripUndefined(value)
        : value;
    }
  }
  return cleaned;
}
